import { Component, type ReactNode } from 'react';
import * as Sentry from '@sentry/react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    Sentry.captureException(error, {
      contexts: { react: { componentStack: errorInfo.componentStack } },
    });
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback || (
        <div className="min-h-screen flex items-center justify-center bg-[#0a0a0a]">
          <div className="text-center max-w-md px-6">
            <h1 className="text-2xl font-bold text-[#f1f5f9] mb-4">Something went wrong</h1>
            <p className="text-[#94a3b8] mb-2">We've encountered an unexpected error.</p>
            <p className="text-[#64748b] text-sm mb-6">
              {this.state.error?.message || 'Please try refreshing the page.'}
            </p>
            <div className="flex items-center justify-center gap-4">
              <button
                onClick={() => window.location.reload()}
                className="px-5 py-2.5 rounded-lg bg-[#e5ac41] text-[#0a0a0a] font-semibold hover:bg-[#d39737] transition-all"
              >
                Refresh Page
              </button>
              <a
                href="/"
                className="px-5 py-2.5 rounded-lg border border-[#1a1a1a] text-[#94a3b8] hover:text-[#f1f5f9] hover:border-[#64748b] transition-all"
              >
                Go Home
              </a>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
